import { useState, type FormEvent } from 'react';
import { errorStatus } from '@portal/platform/http';
import { useSession } from '@portal/platform/session';
import { AppLink } from '../app-link';
import { useShellTexts } from '../i18n/use-shell-texts';
import { inputClass } from '../kit/form/input';
import { fill } from '../i18n/shell-texts';
import { Button } from '../kit/button';
import { Icon } from '../kit/icon';
import { usePortalBranding } from '../shell/branding';
import { Alert, AuthCard, Field } from './auth-card';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Log in. On success `onLoggedIn` decides where to go (the app follows a safe `returnUrl`, else
 * the front page). Ported from portal-ui-lib's LoginComponent minus the "remember me" box, which
 * did nothing.
 */
export function LoginPage({
  onLoggedIn,
  passwordReset = false,
}: {
  onLoggedIn: () => void;
  /** Arrived from a completed password reset. */
  passwordReset?: boolean;
}) {
  const t = useShellTexts().login;
  const session = useSession();
  const branding = usePortalBranding();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailError = touched && !EMAIL.test(email.trim()) ? t.emailInvalid : null;
  const passwordError = touched && password.length === 0 ? t.passwordRequired : null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);
    if (!EMAIL.test(email.trim()) || password.length === 0) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const identity = await session.login({ email: email.trim(), password });
      if (identity) {
        onLoggedIn();
      } else {
        setError(t.failed);
      }
    } catch (failure) {
      const status = errorStatus(failure);
      setError(status === 401 ? t.wrongCredentials : status === 403 ? t.accountLocked : t.failed);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title={t.title}
      subtitle={fill(t.subtitle, { brand: branding.name }).replace(/\s+/g, ' ')}
    >
      {passwordReset ? <Alert tone="success">{t.passwordResetDone}</Alert> : null}
      <form onSubmit={submit} noValidate>
        <Field id="email" label={t.email} error={emailError} addon="@" hideLabel>
          <input
            id="email"
            type="email"
            autoComplete="username"
            className={inputClass}
            placeholder={t.emailPlaceholder}
            value={email}
            onChange={event => setEmail(event.target.value)}
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? 'email-error' : undefined}
          />
        </Field>
        <Field
          id="password"
          label={t.password}
          error={passwordError}
          addon={<Icon name="lock" />}
          hideLabel
        >
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            className={inputClass}
            placeholder={t.passwordPlaceholder}
            value={password}
            onChange={event => setPassword(event.target.value)}
            aria-invalid={passwordError ? true : undefined}
            aria-describedby={passwordError ? 'password-error' : undefined}
          />
        </Field>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button type="submit" variant="primary" className="px-6" loading={submitting}>
            {submitting ? t.submitting : t.submit}
          </Button>
          <AppLink href="/forgot-password" className="text-sm text-brand hover:text-brand-dark">
            {t.forgotPassword}
          </AppLink>
        </div>
      </form>
      {error ? (
        <Alert tone="error" className="mt-6 mb-0">
          {error}
        </Alert>
      ) : null}
    </AuthCard>
  );
}
